import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../Button/Button.tsx';
import { TextField } from '../../TextField/TextField.tsx';
import { StyledCloseIcon } from '../components/Icons.styled.tsx';
import { BULK_SPLITER } from '../constants.ts';
import { getBulkPlaceholder } from '../utils/utils.ts';
import { LIST_HEADER, LIST_HEIGHT } from '../virtualList/constants.ts';

import { StyledBulkActionContainer } from './BulkActionContainerDiv.styled.tsx';
import { StyledBulkCloseButton } from './BulkCloseButton.styled.tsx';
import { StyledBulkContainerDiv } from './BulkContainerDiv.styled.tsx';
import { StyledBulkContentContainerDiv } from './BulkContentContainerDiv.styled.tsx';
import { InvalidItems } from './InvalidItems/InvalidItems.tsx';
import type { BulkValidationReturnType, InvalidItemsError } from './types.ts';

const StyledBulkTitleTypography = styled(Typography)`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  margin-left: ${({ theme }) => theme.spacing(1.5)};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const StyledTextField = styled(TextField)`
  flex-grow: 1;
  min-height: 260px;

  .MuiInput-root {
    height: 100%;
  }

  .MuiInput-root > textarea:first-of-type {
    height: 100% !important;
  }
`;

interface BulkAddProps {
  inputLabel?: string;
  isDisabled: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onApply: (items: string[]) => void;
  validate: (value: string[]) => BulkValidationReturnType;
}

export function BulkAdd({ onClose, onApply, validate, isDisabled, onCancel, inputLabel }: BulkAddProps) {
  const [bulkValue, setBulkValue] = useState('');
  const [isAddDisabled, setAddDisabled] = useState(true);
  const [bulkErrors, setBulkErrors] = useState<InvalidItemsError[]>([]);
  const [bulkValidItems, setBulkValidItems] = useState<string[]>([]);
  const [validating, setValidating] = useState(false);
  const { t } = useTranslation();

  const onValidate = useCallback(async () => {
    setValidating(true);
    const items = bulkValue
      .split(BULK_SPLITER)
      .map(value => value.trim())
      .filter(value => !!value);

    const { errors, validItems } = await validate(items);
    setBulkErrors(errors);
    setBulkValidItems(validItems);

    const validItemsReformated = validItems.join('\n');
    setBulkValue(validItemsReformated);
    setAddDisabled(!validItemsReformated);
    setValidating(false);
  }, [bulkValue, validate]);

  const onChange = useCallback(event => {
    setBulkValue(event.target.value);
    setAddDisabled(true);
  }, []);

  const inputPlaceholder = getBulkPlaceholder(t);

  return (
    <StyledBulkContainerDiv listHeight={LIST_HEIGHT + LIST_HEADER}>
      <StyledBulkCloseButton data-test="bulk-close" variant="text" onClick={onClose}>
        <StyledCloseIcon /> {t('close')}
      </StyledBulkCloseButton>

      <StyledBulkContentContainerDiv>
        {!!bulkErrors.length && <InvalidItems errors={bulkErrors} />}

        <StyledBulkTitleTypography variant="caption">
          {inputLabel ? inputLabel : t('Bulk Add')}
        </StyledBulkTitleTypography>
        <StyledTextField
          dataTest="transfer-list-bulk-input"
          multiline
          fullWidth
          slotProps={{ input: { disableUnderline: true } }}
          placeholder={inputPlaceholder}
          onChange={onChange}
          value={bulkValue}
          disabled={validating || isDisabled}
          rows={1}
        />
      </StyledBulkContentContainerDiv>

      <StyledBulkActionContainer>
        <Button disabled={isAddDisabled || isDisabled} onClick={() => onApply(bulkValidItems)} color="primary">
          {t('Bulk Add')}
        </Button>
        <Button loading={validating} onClick={onValidate} disabled={isDisabled}>
          {t('Validate')}
        </Button>

        {onCancel && (
          <Button data-test="bulk-cancel-button" variant="text" onClick={onCancel} disabled={isDisabled}>
            {t('Cancel')}
          </Button>
        )}
      </StyledBulkActionContainer>
    </StyledBulkContainerDiv>
  );
}
